import React, { useEffect } from "react";
import { useNavigate, useRoutes } from "react-router-dom";
import _, { get } from "lodash";
import { RankedTester } from "@jsonforms/core";
import { getHeaders } from "@apollo/jsonforms-react-spectrum-renderers/src/util/commonUtils";
import { useSelector } from "react-redux";

import useFetch from "../../hooks/useFetch";
import JSONForm from "../../JSONForm";
import {
  APITemplateProps,
  CommonJsonHeaders,
  DynamicData,
  LifecycleHooks,
} from "../../types/Pages";
import {
  getFedsConfig,
  loadExternalFiles,
  printSchemaVersion,
  addCommonComponent,
  addCommonJSONHeader,
  mergeUiSchema,
} from "../../utils/commonUtils";
import { DYNAMIC_FORMS } from "../../utils/constants";
import Loader from "../Loader/Loader";
import * as konsole from "../../utils/konsole";

declare const window: any;

function APITemplate({
  pages,
  storybook = false,
  lifecycleHooks,
  commonJsonHeaders,
  renderers,
  dynamicData,
  currentEnvironment = "dev",
  defaultRoute,
  includeAdvancedRenderers,
}: {
  pages: APITemplateProps;
  storybook?: boolean;
  lifecycleHooks: LifecycleHooks;
  commonJsonHeaders: CommonJsonHeaders;
  dynamicData?: DynamicData;
  currentEnvironment: string;
  defaultRoute?: string;
  renderers?: {
    tester: RankedTester;
    renderer: any;
  }[];
  includeAdvancedRenderers?: boolean;
}) {
  const navigate = useNavigate();
  const endpoint = pages?.endpoint;
  const templateId = pages?.templateId;
  const headerTemplates = pages?.headerTemplates;
  const footerTemplates = pages?.footerTemplates;

  const templateEndpoint = endpoint + templateId;

  const templateDataKey = pages?.templateDataKey;
  const isMultipage = pages?.isMultiPage || false;
  /** get values from initial redux state */
  const gNavFromContext = useSelector((state: any) => state?.reduxState?.gNav);
  let headers: any = {};
  if (commonJsonHeaders?.apiHeaders) {
    headers = getHeaders(commonJsonHeaders?.apiHeaders);
    headers.append("content-type", "application/json;charset=UTF-8");
  }
  const { data, error }: any = useFetch(templateEndpoint, { headers });
  if (error) {
    konsole.log(`${DYNAMIC_FORMS}: Unable to fetch JSON: ${error}`);
    if (defaultRoute && error === "Error: Unauthorized") {
      navigate(defaultRoute);
    }

    return null;
  }

  let headerJsonRenderer = "";
  let footerJsonRender = "";
  if (headerTemplates) {
    let headerEndpoint = !window.adobeIMS?.isSignedInUser()
      ? headerTemplates?.header
      : headerTemplates?.authHeader || "";
    if (headerEndpoint) {
      headerEndpoint = endpoint + headerEndpoint;
      const { data: headerJson, error: jsonError } = useFetch(headerEndpoint, {
        headers,
      });
      if (jsonError) {
        konsole.log(
          `${DYNAMIC_FORMS}: Unable to fetch Header JSON: ${jsonError}`
        );
        headerJsonRenderer = "";
      } else if (headerJson) {
        headerJsonRenderer = get(headerJson, "formData.options.uischema") || "";
      }
    }
  }
  if (footerTemplates) {
    let footerEndpoint = !window.adobeIMS?.isSignedInUser()
      ? footerTemplates?.footer
      : footerTemplates?.authFooter || "";
    if (footerEndpoint) {
      footerEndpoint = endpoint + footerEndpoint;
      const { data: footerJson, error: footerError } = useFetch(
        footerEndpoint,
        { headers }
      );
      if (footerError) {
        konsole.log(
          `${DYNAMIC_FORMS}: Unable to fetch Footer JSON: ${footerError}`
        );
        footerJsonRender = "";
      } else if (footerJson) {
        footerJsonRender = get(footerJson, "formData.options.uischema") || "";
      }
    }
  }

  let pageData = data;

  if (data) {
    konsole.log(`${DYNAMIC_FORMS}: JSON Data fetched successfully: `, { data });
    if (templateDataKey) {
      pageData = get(data, templateDataKey);
    }
    pageData = addCommonJSONHeader(
      pageData,
      commonJsonHeaders,
      currentEnvironment
    );
  }
  /** if gnav config is set in initial redux state it will be considered  */
  const fedsConfig = gNavFromContext || getFedsConfig(pageData);

  const external = pageData?.options?.external;

  // console.log({ pageData });

  useEffect(() => {
    if (external) {
      loadExternalFiles(external);
    }
  }, [external]);

  let config: any = {
    ...pageData?.config,
    lifecycleHooks,
    fedsConfig,
    dynamicData,
  };

  window.DynamicForms.pages = pageData;

  const imsData = pageData?.options?.imsData;
  window.imsConfig = imsData;

  if (pageData?.version) {
    printSchemaVersion(pageData.version);
  }

  if (!data && !error) return <Loader fullScreen />;

  if (error) {
    konsole.log(`${DYNAMIC_FORMS}: Unable to fetch JSON: ${error}`);

    return null;
  }

  let singlePage;
  let multiPages;
  const components = pageData?.options?.components;

  if (isMultipage) {
    multiPages = pageData?.elements;
  } else {
    singlePage = pageData?.options;
  }
  const container = pageData?.options?.container;
  const header = pageData?.options?.header;
  const footer = pageData?.options?.footer;

  if (storybook) {
    const storybookPage = isMultipage ? pageData?.elements[0] : pageData;
    let updatedUiSchema = addCommonComponent(
      storybookPage?.uischema,
      components
    );

    updatedUiSchema = mergeUiSchema(
      updatedUiSchema,
      header,
      footer,
      container,
      headerJsonRenderer,
      footerJsonRender
    );

    const commonTranslations = commonJsonHeaders?.translations;
    const pageTranslations = storybookPage?.translations;
    const combinedTranslations = _.mergeWith(
      commonTranslations,
      pageTranslations
    );
    config = { ...config, translations: combinedTranslations };

    return (
      <JSONForm
        schema={storybookPage?.schema}
        uischema={updatedUiSchema}
        data={storybookPage?.data}
        config={config}
        renderers={renderers}
        authenticated={storybookPage?.authenticated}
        includeAdvancedRenderers={includeAdvancedRenderers}
      />
    );
  }

  if (singlePage) {
    let updatedUiSchema = addCommonComponent(singlePage?.uischema, components);
    updatedUiSchema = mergeUiSchema(
      updatedUiSchema,
      header,
      footer,
      container,
      headerJsonRenderer,
      footerJsonRender
    );

    const commonTranslations = commonJsonHeaders?.translations;
    const pageTranslations = singlePage?.translations;
    const combinedTranslations = _.mergeWith(
      commonTranslations,
      pageTranslations
    );
    config = { ...config, translations: combinedTranslations };

    return (
      <JSONForm
        schema={singlePage?.schema}
        uischema={updatedUiSchema}
        data={singlePage?.data}
        config={config}
        renderers={renderers}
        authenticated={singlePage?.authenticated}
        includeAdvancedRenderers={includeAdvancedRenderers}
      />
    );
  }
  const routes = useRoutes(
    multiPages.map((page: any) => {
      const {
        schema,
        uischema,
        data: dataObj,
        authenticated = false,
      } = page.options;
      const uiWithCommonComponent = addCommonComponent(uischema, components);
      const updatedUiSchema = mergeUiSchema(
        uiWithCommonComponent,
        header,
        footer,
        container,
        headerJsonRenderer,
        footerJsonRender
      );
      const path = pages?.templatePath || page?.options?.path || templateId;

      const commonTranslations = commonJsonHeaders?.translations;
      const pageTranslations = page?.options?.translations;
      const combinedTranslations = _.mergeWith(
        commonTranslations,
        pageTranslations
      );
      config = { ...config, translations: combinedTranslations };

      return {
        path,
        element: (
          <JSONForm
            schema={schema}
            uischema={updatedUiSchema}
            data={dataObj}
            config={config}
            authenticated={authenticated}
            renderers={renderers}
            includeAdvancedRenderers={includeAdvancedRenderers}
          />
        ),
      };
    })
  );
  return <div>{routes}</div>;
}

export default APITemplate;

APITemplate.defaultProps = {
  storybook: false,
  dynamicData: null,
  defaultRoute: "",
  renderers: [],
  includeAdvancedRenderers: false,
};
